import { z } from "zod";
import { twinEntityContactSchema } from "./twin-entities.js";
/**
 * What the Twin knows about the things identifiers name (`twin.context.resolve`): a client passes
 * the identifiers in front of the person (an email address, a domain, a company or VAT number, a
 * code, a page's address, an entity ID), never text, and gets back the entities they belong to as
 * references with short facts. It reads the entity layer's own keys, as the person may read them,
 * and runs no model.
 */
export const twinContextIdentifierKindSchema = z.enum([
    /** A person's address: the entity it is, and those whose mail domain or website its domain is. */
    "email",
    /** A domain or host: entities whose mail domain or website it is. */
    "domain",
    /** A page's address: entities whose website its host is. */
    "url",
    "company_number",
    "vat_number",
    /** A code a system gives a record, such as an account code. */
    "reference",
    /** An entity's ID (ent_…), from an earlier answer. */
    "entity",
    /**
     * An organisation's name, as an email's sender gives it: the one organisation whose name it is,
     * read the same whatever its case, punctuation, spacing, leading "the" or legal form (Ltd,
     * Limited, Inc), and none when it names several. Never a part of a name or a guess. A client sends
     * a sender's display name only when their address and its domain name nothing (Archie, 17 October
     * 2026).
     */
    "name",
]);
/** The longest name a lookup takes: a display name, never text. */
export const TWIN_CONTEXT_NAME_MAX_LENGTH = 200;
const NOT_PLAIN_TEXT = /[\p{Cc}\p{Cf}\p{Co}\p{Cs}\p{Zl}\p{Zp}<>]/u;
const ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const PAGE_ADDRESS = /^[a-z][a-z0-9+.-]*:\/\//iu;
/**
 * Whether a value may be looked up as a name: one line of plain text up to 200 characters, with a
 * letter or a digit, no markup or control characters, and not an address or a page's address, which
 * have kinds of their own.
 */
export function isTwinContextName(value) {
    const name = value.trim();
    return name.length > 0 && name.length <= TWIN_CONTEXT_NAME_MAX_LENGTH && /[\p{L}\p{N}]/u.test(name)
        && !NOT_PLAIN_TEXT.test(name) && !ADDRESS.test(name) && !PAGE_ADDRESS.test(name);
}
export const twinContextIdentifierSchema = z.object({
    kind: twinContextIdentifierKindSchema,
    value: z.string().trim().min(1).max(500),
}).strict().superRefine((identifier, context) => {
    if (identifier.kind === "name" && !isTwinContextName(identifier.value)) {
        context.addIssue({
            code: "custom",
            path: ["value"],
            message: `A name is one line of plain text of up to ${TWIN_CONTEXT_NAME_MAX_LENGTH} characters, not an address.`,
        });
    }
});
export const twinContextResolveInputSchema = z.object({
    identifiers: z.array(twinContextIdentifierSchema).min(1).max(20),
}).strict();
/** The entity key an identifier matched on, `entity` for an entity ID, or `name` for an organisation's name. */
export const twinContextMatchBySchema = z.enum([
    "entity",
    "email",
    "email_domain",
    "website_domain",
    "company_number",
    "vat_number",
    "provider_code",
    "name",
]);
const entityIdSchema = z.string().regex(/^ent_[a-f0-9]{24}$/u);
export const twinContextEntitySchema = z.object({
    id: entityIdSchema,
    entityType: z.string().min(1),
    typeLabel: z.string(),
    displayName: z.string(),
    /** The date a record-like entity (an engagement) occurred on. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).optional(),
    /** What it is to the organisation (a customer, a supplier), as the records the person may read say. */
    roles: twinEntityContactSchema.shape.roles,
    /** The entity's page in the organisation's MLX app. */
    path: z.string().startsWith("/workspace/entities/"),
    /** How much refers to it, all time or now: counts of its records and amounts, each currency apart. */
    facts: z.array(z.object({
        label: z.string(),
        measure: z.string().optional(),
        period: z.string(),
        value: z.number(),
        currency: z.string().regex(/^[A-Z]{3}$/u).optional(),
    }).strict()).max(12),
    /** The entities linked to it, a group each, with up to three of each group. */
    linked: z.array(z.object({
        label: z.string(),
        entityType: z.string().min(1),
        count: z.number().int().nonnegative(),
        sample: z.array(z.object({ id: z.string(), displayName: z.string() }).strict()).max(3),
    }).strict()).max(10),
    /** Open work: its records an attention rule raised that still need a look ("Invoice INV-0042 is overdue"). */
    openWork: z.array(z.object({
        title: z.string(),
        reason: z.string(),
        ruleLabel: z.string(),
        raisedAt: z.number().int().nonnegative(),
    }).strict()).max(10),
    /** Recent activity: how many of its records there are, and the latest year they fall in. */
    activity: z.object({
        records: z.number().int().nonnegative().optional(),
        latestYear: z.string().regex(/^\d{4}$/u).optional(),
    }).strict(),
}).strict();
export const twinContextResolveResponseSchema = z.object({
    /** One per identifier, in the order asked: the entities it names, and the key each matched on. */
    matches: z.array(z.object({
        identifier: twinContextIdentifierSchema,
        entities: z.array(z.object({ entityId: entityIdSchema, by: twinContextMatchBySchema }).strict()),
        /** A name that names several organisations the person may see: it names none of them, never a guess. */
        ambiguous: z.literal(true).optional(),
    }).strict()),
    /** Each entity matched, once, in the order first matched; an entity a name found after every identifier's. */
    entities: z.array(twinContextEntitySchema).max(10),
    /** More entities matched than `entities` describes. */
    truncated: z.boolean(),
}).strict();
